import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/SetupIntentsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the setup-intents list.
//
// ⚠️ MONEY PATH. A SetupIntent attaches a payment method for FUTURE charges —
// it is the object that authorises later off-session billing. Only the
// LIST/TABLE layer is touched here. No confirm/cancel semantics, no idempotency
// key, and no `clientSecret` handling is changed; `add` and `update` in the
// service keep their existing call shape (the update payload is whitelisted,
// which narrows what is sent — it never widens it).
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['setupIntents', {customerId}, currentToken]` — a bare string plus a
// PAGE TOKEN, which makes the cache page-shaped rather than list-shaped: every
// page of the same list occupied its own cache entry, and going "back" refetched
// rather than reading the page already in memory.
export const setupIntentsKeys = {
    all: ['setup-intents'] as const,
    lists: () => [...setupIntentsKeys.all, 'list'] as const,
    list: (customerId: string, filters: Record<string, string> = {}) =>
        [...setupIntentsKeys.lists(), customerId, {filters}] as const,
    details: () => [...setupIntentsKeys.all, 'detail'] as const,
    detail: (id: string) => [...setupIntentsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which could
 * hold exactly one page at a time. The query document already declared `$limit`
 * and `$nextToken` and selected `nextToken`, so the API side was fine — the
 * client was the limitation.
 *
 * ⚠️ `customerId` IS THE ONLY SERVER-SIDE FILTER. `SetupIntentsFilterInput`
 * declares `{customerId: String}` and NOTHING ELSE — verified against
 * flexinet-stripe-api/src/appsync-api/src/schema/features/setupIntents.graphql.
 * Status and usage therefore CANNOT be narrowed by the API; they are applied
 * client-side over the loaded window. See useSetupIntentsFilter.
 *
 * ⚠️ Unlike payment-methods, `customerId` is OPTIONAL (`String`, not `String!`),
 * so this list runs unscoped on the index page. An empty filter object is sent
 * rather than `{customerId: ''}` — an empty string is a value the API would try
 * to match, not an absence.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 */
export const useSetupIntentsInfiniteQuery = (
    customerId?: string,
    filters: Record<string, string> = {},
) => {
    // Only `customerId` reaches the server; every other filter is client-side,
    // so it must NOT be put in the request or AppSync silently ignores it and
    // the filter looks like it works while doing nothing.
    const serverFilter: Record<string, string> = customerId
        ? {customerId}
        : {};

    return useInfiniteQuery({
        // `filters` is still in the KEY even though it is applied client-side:
        // the key identifies the view, and leaving it out is harmless only
        // until a filter becomes server-side. Keeping it costs one cache entry
        // per filter combination and removes a whole class of stale-view bug.
        queryKey: setupIntentsKeys.list(customerId ?? '', filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(serverFilter, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

/**
 * Batch delete (cancel). The whole module key is invalidated on success so the
 * table reflects the removal without a manual reload (§8).
 *
 * ⚠️ MONEY PATH: the underlying `deleteRecord` service call is UNCHANGED. This
 * wraps the existing behaviour in structured keys and a notification, nothing
 * more — no new idempotency handling, no changed cancel semantics.
 *
 * ⚠️ The message is PLURALISED off the id count. The previous implementation
 * always said "Setup intent deleted" even when five were removed, which reads
 * as a partial failure to an operator who selected five rows.
 */
export const useDeleteSetupIntentsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Setup intent deleted'
                    : `${ids.length} setup intents deleted`,
            );
            queryClient.invalidateQueries({queryKey: setupIntentsKeys.all});
        },
        onError: notifyError,
    });
};
